import { getSpreadDimensions } from '../../components/BookDesign/constants';
import { PAGE_SIZES, calculateDimensions } from '../../constants/pageSizes';

/**
 * Calculates accurate page dimensions for PDF generation that match the design view
 * @param {string} pageSize - The key of the page size (e.g., 'us-trade', 'landscape-custom')
 * @param {Object} options - Additional options for PDF generation
 * @param {boolean} [options.includeBleed=true] - Whether to include bleed area (0.125" on each side)
 * @param {number} [options.bleedSize=0.125] - Size of bleed in inches (default: 0.125")
 * @param {Object} [options.customDimensions] - Optional custom dimensions {width, height, orientation}
 * @returns {Object} Object containing width, height, scale factors, and bleed information
 */
export function calculatePageDimensions(pageSize = 'us-trade', options = {}) {
  // Get the display dimensions used in the design view
  let displayDimensions;
  const customDimensions = options.customDimensions;
  
  if (customDimensions) {
    // Use custom dimensions if provided
    displayDimensions = calculateDimensions(null, customDimensions);
  } else {
    displayDimensions = getSpreadDimensions(pageSize || 'us-trade');
  }
  
  // Get the actual physical dimensions in inches
  let physicalSize;
  if (customDimensions) {
    physicalSize = {
      width: customDimensions.width,
      height: customDimensions.height,
      orientation: customDimensions.orientation || 'portrait'
    };
  } else {
    physicalSize = PAGE_SIZES[pageSize] || PAGE_SIZES['us-trade'];
  }
  
  // Check if this is a landscape orientation
  const isLandscape = physicalSize.orientation === 'landscape' || 
                     (physicalSize.width > physicalSize.height && physicalSize.orientation !== 'portrait');
  
  // Define bleed size in inches (0.125" on each side = 0.25" total increase)
  const bleedSizeInches = options.bleedSize || 0.125;
  const includeBleed = options.includeBleed !== undefined ? options.includeBleed : true;
  
  // Calculate bleed in points (72 points per inch)
  const bleedSizePoints = bleedSizeInches * 72;
  
  // Convert physical dimensions to PDF points with optional bleed
  let pdfWidth = physicalSize.width * 72;  // Base width in points
  let pdfHeight = physicalSize.height * 72; // Base height in points
  
  // Add bleed to both dimensions if requested
  if (includeBleed) {
    pdfWidth += (bleedSizePoints * 2); // Add bleed to left and right
    pdfHeight += (bleedSizePoints * 2); // Add bleed to top and bottom
  }
  
  // Special case for Lulu landscape-custom size
  const isLandscapeCustom = pageSize === 'landscape-custom' || 
    (physicalSize.width === 12.382 && physicalSize.height === 9.25 && physicalSize.orientation === 'landscape');

  let widthScale, heightScale, scale, scaleY;
  
  if (isLandscapeCustom) {
    // For landscape-custom (Lulu cover), calculate precise mapping
    // Based on actual pixel-to-point conversion needed for exact positioning
    
    // We need to map the design view's pixel dimensions to the PDF's point dimensions
    // For the specific case of landscape-custom (12.382 × 9.25 inches)
    // PDF dimensions in points: 891.5 x 666 points
    const exactPDFWidth = 891.5; // 12.382 * 72
    const exactPDFHeight = 666;  // 9.25 * 72
    
    // Calculate exact scales based on display dimensions vs exact PDF dimensions
    widthScale = exactPDFWidth / displayDimensions.width;
    heightScale = exactPDFHeight / displayDimensions.height;
    
    console.log('Exact PDF dimensions for landscape-custom:', {
      pdfPoints: { width: exactPDFWidth, height: exactPDFHeight },
      designPixels: { width: displayDimensions.width, height: displayDimensions.height },
      calculatedScales: { widthScale, heightScale }
    });
    
    // Use these precise scales for accurate positioning
    scale = widthScale;
    scaleY = heightScale;
  } else {
    // Calculate standard scale factors for other sizes
    // This ensures elements are positioned and sized proportionally
    widthScale = pdfWidth / displayDimensions.width;
    heightScale = pdfHeight / displayDimensions.height;
    
    // For landscape orientation, use appropriate scaling to maintain proportions
    scale = isLandscape ? heightScale : widthScale;
    scaleY = isLandscape ? heightScale : heightScale;
  }
  
  // Log the dimensions for debugging
  console.log(`PDF Dimensions for ${pageSize}${includeBleed ? ' with bleed' : ''}:`, {
    physicalSize,
    orientation: isLandscape ? 'landscape' : 'portrait',
    displayDimensions,
    bleed: includeBleed ? { inches: bleedSizeInches, points: bleedSizePoints } : 'none',
    pdfDimensions: { width: pdfWidth, height: pdfHeight },
    scales: { widthScale, heightScale, scale, scaleY }
  });
  
  return {
    width: pdfWidth,
    height: pdfHeight,
    scale: scale, // Use appropriate scale factor based on orientation
    scaleY: scaleY, // Use proper Y scale factor
    isLandscape: isLandscape, // Flag for landscape orientation
    bleed: includeBleed ? bleedSizePoints : 0, // Bleed size in points
    bleedInches: bleedSizeInches, // Bleed size in inches
    trimWidth: physicalSize.width * 72, // Original width without bleed
    trimHeight: physicalSize.height * 72, // Original height without bleed
    orientation: isLandscape ? 'landscape' : 'portrait'
  };
}